import { Suspense, lazy, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, Map as MapIcon } from 'lucide-react';
import { fetchAreaNetwork } from '@/hooks/useTickets';
import type { NetworkMapLayer } from '@/components/network/NetworkMap';
import type { AreaNetworkResponse } from '@uniticket/shared';

/** Leaflet only loads once the panel is actually opened. */
const NetworkMap = lazy(() => import('@/components/network/NetworkMap'));

/**
 * The Build ticket's area map: the greenfield outline plus the Cocon network around it.
 *
 * A thin adapter — all the map behaviour lives in the reusable NetworkMap component. This only
 * knows which layers a build area has and what to call them. Collapsed by default so the DMP
 * query and Leaflet download are paid for only when someone wants to look.
 */
export function BuildAreaMapPanel({ areaId }: { areaId: string }) {
  const { t } = useTranslation(['tickets', 'common']);
  const [open, setOpen] = useState(false);
  const [data, setData] = useState<AreaNetworkResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  /**
   * Which area has already been requested. A ref, NOT state, and `loading`/`data` are kept out of
   * the dependency array: putting them there makes setLoading(true) re-run the effect, fire the
   * cleanup, and cancel the very request it just started — leaving the panel spinning forever.
   */
  const requestedRef = useRef<string | null>(null);

  useEffect(() => {
    if (!open || requestedRef.current === areaId) return;
    requestedRef.current = areaId;

    let cancelled = false;
    setLoading(true);
    setError(false);
    fetchAreaNetwork(areaId)
      .then((res) => { if (!cancelled) setData(res.data); })
      .catch(() => { if (!cancelled) { setError(true); requestedRef.current = null; } })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [open, areaId]);

  /**
   * The area outline is for everyone who can read the ticket; Unifiber's passive network on top of
   * it is gated by the operator's `canViewNetwork` capability. When the server withholds it we pass
   * NO layers at all rather than empty ones — a toggle that reveals nothing is worse than no toggle.
   * The server is the gate; this only avoids advertising something the caller cannot have.
   */
  const networkVisible = data?.networkVisible !== false;

  // Colours match EnrichmentMap so the two maps read the same way across the app.
  const layers: NetworkMapLayer[] = networkVisible
    ? [
        { id: 'cables', label: t('tickets:areaMap.cables'), kind: 'line', color: '#1e40af', colorProperty: 'duct_color', data: data?.cables, popupFields: ['name', 'item_type', 'duct_name'] },
        { id: 'ducts', label: t('tickets:areaMap.ducts'), kind: 'line', color: '#7e22ce', data: data?.ducts, popupFields: ['name', 'item_type'] },
        { id: 'enclosures', label: t('tickets:areaMap.enclosures'), kind: 'point', color: '#166534', data: data?.enclosures, popupFields: ['name', 'type', 'item_type'] },
        // Points of presence are regional, so this is usually empty at greenfield zoom — off by default.
        { id: 'pops', label: t('tickets:areaMap.pops'), kind: 'point', color: '#b91c1c', data: data?.pops, defaultVisible: false, popupFields: ['name', 'type', 'status'] },
      ]
    : [];

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-slate-50 transition-colors"
      >
        <span className="flex items-center gap-2">
          <MapIcon className="w-4 h-4 text-slate-400" />
          <span className="text-sm font-bold text-slate-800">{t('tickets:areaMap.title')}</span>
          <span className="text-xs font-mono text-slate-400">{areaId}</span>
        </span>
        <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="px-6 pb-6">
          {error ? (
            <p className="text-sm text-slate-500 py-6 text-center">{t('tickets:areaMap.error')}</p>
          ) : (
            <Suspense fallback={<div className="h-96 rounded-lg bg-slate-50" />}>
              <NetworkMap
                layers={layers}
                outline={data ? { geometry: data.area.geometry } : null}
                bbox={data?.area.bbox}
                loading={loading}
                loadingLabel={t('tickets:areaMap.loading')}
                // "No network in this area" would be a lie when the layers are simply withheld.
                emptyLabel={networkVisible ? t('tickets:areaMap.noNetwork') : undefined}
              />
            </Suspense>
          )}
          <p className="mt-2 text-[11px] text-slate-400">
            {networkVisible ? t('tickets:areaMap.hint') : t('tickets:areaMap.outlineOnly')}
          </p>
        </div>
      )}
    </div>
  );
}
